import { useEffect, useRef, useState, type RefObject } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import * as AlertDialog from "@radix-ui/react-alert-dialog";

import { humanizeLabel } from "../data/alert-queue";
import { fetchScheduleCoverageRange, type ScheduleCoverageRow } from "../data/schedule";
import {
  assignMemberToShiftSlot,
  fetchAssignableMembers,
  SAFE_ASSIGN_FAILURE,
  type AssignableMember,
  type AssignOutcome,
} from "../data/schedule-assign";
import { buildCopyWeekPreview, type CopyWeekPreview } from "../data/copy-week-preview";
import { buildWeekGrid, shiftWeek, weekDays, weekStartIso, type BuilderSlot } from "../data/schedule-builder";
import {
  cancelShiftTemplateWeek,
  cancelShiftSlot,
  copyShiftWeek,
  SAFE_CANCEL_FAILURE,
  SAFE_COPY_FAILURE,
  SAFE_TEMPLATE_WEEK_CANCEL_FAILURE,
  type CancelTemplateWeekOutcome,
  type CopyWeekOutcome,
} from "../data/schedule-publish";
import {
  fetchSchedulePublishReview,
  publishShiftTemplateWeek,
  SAFE_PUBLISH_WEEK_FAILURE,
  type SchedulePublishReceipt,
  type SchedulePublishReview as SchedulePublishReviewFacts,
} from "../data/schedule-publish-review";
import { SchedulePublishReview } from "../components/SchedulePublishReview";
import {
  buildPublishReceiptLine,
  resolveSchedulePublishGate,
} from "../lib/schedule-publish-gate";
import {
  fetchShiftTemplates,
  SCHEDULE_COLORS_UNAVAILABLE,
  type ShiftTemplateRow,
} from "../data/schedule-templates";
import { buildPublishPreview } from "../data/publish-preview";
import { TemplateColorChip } from "../components/TemplateColorChip";
import { RowMenu, rowMenuItems } from "../components/RowMenu";
import { OPEN_FILL_MODE_COPY, type OpenFillMode } from "../../../../packages/shared/src/schedule-color.ts";
import { SLOT_TONE_COPY, type SlotTone } from "../data/schedule-tone";
import { useReadModel } from "../data/useReadModel";
import { guardRefTitle, resolveGuardName } from "../lib/guard-name";
import { Link } from "react-router-dom";
import { scheduleTimesheetsPath } from "../lib/schedule-timesheets-route";
import {
  fetchScheduleSlotLineage,
  replaceScheduleSeatAssignment,
  reviseScheduleSlotBounds,
  SAFE_LINEAGE_READ_FAILURE,
  SAFE_REPLACE_FAILURE,
  SAFE_REVISE_BOUNDS_FAILURE,
  SCHEDULE_SEAT_STARTED,
  SCHEDULE_WEEK_ENDED,
  type ReplaceSeatOutcome,
  type ReviseBoundsOutcome,
  type ScheduleLineageSeat,
  type ScheduleSlotLineage,
} from "../data/schedule-seat-lineage";
import { fromLocalInput, toLocalInput } from "./FeedComposer";
import { ManagerCalendar } from "../features/manager-calendar/ManagerCalendar";
import { ScheduleOverlay, useScheduleBusy } from "../features/manager-calendar/ScheduleOverlay";
import { StaffWeekSchedule } from "../features/manager-calendar/StaffWeekSchedule";
import { CalendarShiftPanel } from "../features/manager-calendar/CalendarShiftPanel";
import { CoverageViews } from "../features/manager-calendar/CoverageViews";
import { useCoverageNavigation } from "../features/manager-calendar/use-coverage-navigation";

// Schedule Builder (CONSOLE-SCHEDULE-BUILDER-SCAFFOLD + ASSIGN + PUBLISH + CANCEL). The
// approved standalone's week grid rendered over live coverage rows: posts down, Mon..Sun
// across on the ISO service week, all six slot tones, credential chips, week navigation.
// Writes - assignment (assign_member_to_shift_slot, membership ids only), template publish
// for the visible week (publish_shift_template_week, behind the shared review gate and
// idempotent server-side), and slot cancel (cancel_shift_slot) -
// run ONLY behind schedule.manage and explicit confirmation (test-pinned: zero rpc calls
// otherwise). Copy-week and template editing stay disabled until separately assigned.

function formatDayHeader(dateIso: string, timeZone: string): string {
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(
    new Date(`${dateIso}T12:00:00Z`),
  );
  return `${weekday} ${dateIso.slice(5).replace("-", "/")}`;
}

function SlotCard({
  slot,
  canManage,
  onAssign,
  onCancel,
  onReplace,
  onRevise,
}: {
  slot: BuilderSlot;
  canManage: boolean;
  onAssign: () => void;
  onCancel: () => void;
  onReplace: () => void;
  onRevise: () => void;
}) {
  const showActions =
    canManage &&
    (slot.assignable || slot.cancellable || slot.replaceable || slot.reviseBoundsEligible);
  return (
    <div className={`builder__slot builder__slot--${slot.tone}`}>
      <div className="builder__slot-main">
        <span className="builder__window">{slot.window}</span>
        <span className="builder__fraction">{slot.fraction}</span>
      </div>
      <div className="builder__slot-meta">
        <span className={`schedule__chip schedule__chip--${slot.tone}`}>{toneCopy(slot.tone)}</span>
        {slot.credentials.map((credential) => (
          <span key={credential} className="schedule__chip schedule__chip--credential">
            {humanizeLabel(credential)}
          </span>
        ))}
      </div>
      {showActions ? (
        <div className="builder__slot-actions">
          {slot.assignable ? (
            <button type="button" className="builder__assign" onClick={onAssign}>
              Assign
            </button>
          ) : null}
          {slot.replaceable ? (
            <button type="button" className="builder__assign" onClick={onReplace}>
              Replace
            </button>
          ) : null}
          {slot.reviseBoundsEligible ? (
            <button type="button" className="builder__navbtn" onClick={onRevise}>
              Revise times
            </button>
          ) : null}
          {slot.cancellable ? (
            <button type="button" className="builder__cancel" onClick={onCancel}>
              Cancel slot
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function todayInZone(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(
    new Date(),
  );
}

function toneCopy(tone: SlotTone): string {
  return tone === "open" ? "OPEN" : SLOT_TONE_COPY[tone];
}

/** Slot context for assign, cancel, replace, and revise flows. */
interface AssignTarget {
  slotId: string;
  siteName: string;
  routeName: string;
  window: string;
  fraction: string;
  startsAt: string | null;
  endsAt: string | null;
  headcountRequired: number;
}

interface ScheduleBuilderProps {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  /** Test seam - anchor the initial week to a fixed date instead of today. */
  initialDate?: string;
  /** schedule.manage capability - the assign affordance only exists when true (fail closed). */
  canManage?: boolean;
  calendarMode?: boolean;
  onDefineShift?: (date: string) => void;
}

const scheduleClientKeys = new WeakMap<SupabaseClient, number>();
let nextScheduleClientKey = 0;

/** Keep pending targets and read results inside one client, workspace and access scope. */
export function ScheduleBuilder(props: ScheduleBuilderProps) {
  let clientKey = scheduleClientKeys.get(props.client);
  if (clientKey === undefined) {
    clientKey = ++nextScheduleClientKey;
    scheduleClientKeys.set(props.client, clientKey);
  }
  return <ScheduleBuilderScope key={`${clientKey}:${props.accountId}:${props.timeZone}:${props.canManage === true}`} {...props} />;
}

function ScheduleBuilderScope({ client, accountId, timeZone, pollMs = 30000, initialDate,
  canManage = false, calendarMode = false, onDefineShift }: ScheduleBuilderProps) {
  const [fallbackDate] = useState(initialDate ?? todayInZone(timeZone));
  const [staffPanelOpen, setStaffPanelOpen] = useState(false);
  const [calendarTarget, setCalendarTarget] = useState<{ slot: BuilderSlot; siteName: string; routeName: string; fillMode?: OpenFillMode | undefined } | null>(null);
  const [emptyDate, setEmptyDate] = useState<string | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  // Week cancel moved from a headline button into the overflow menu (Wave 2 board rule 7);
  // the dialog it opens is unchanged, so its open state simply moved up one level.
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  // Held here so the week-cancel dialog always has a live element to return focus to,
  // even if the element that opened it is gone by the time the dialog closes.
  const weekActionsTriggerRef = useRef<HTMLButtonElement>(null);
  const [cancelTarget, setCancelTarget] = useState<AssignTarget | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<AssignTarget | null>(null);
  const [reviseTarget, setReviseTarget] = useState<AssignTarget | null>(null);
  const overlayOpen = bulkCancelOpen || assignTarget !== null || publishOpen || copyOpen || cancelTarget !== null || replaceTarget !== null || reviseTarget !== null || calendarTarget !== null || emptyDate !== null;
  const navigation = useCoverageNavigation(calendarMode, fallbackDate, overlayOpen || staffPanelOpen);
  const selectedDate = navigation.date;
  const weekStart = weekStartIso(selectedDate);
  const days = weekDays(weekStart);
  const { status, data: coverageSnapshot, error, reload } = useReadModel<{ weekStart: string; rows: ScheduleCoverageRow[] }>(
    async () => ({ weekStart, rows: await fetchScheduleCoverageRange(client, accountId, days[0]!, days[6]!) }),
    [client, accountId, weekStart],
    pollMs,
  );
  // A previous week's result cannot become an authoritative zero for a newly selected week.
  const data = coverageSnapshot?.weekStart === weekStart ? coverageSnapshot.rows : null;

  if (status === "loading" && coverageSnapshot === null) {
    return <p className="surface__state" role="status">Loading the schedule week...</p>;
  }
  // A failed refetch must not yank a destructive confirm out from under the operator. The
  // 30s poll and the post-write reload both land here, and swapping the whole surface for
  // an error line while the week-cancel dialog is open would unmount that dialog
  // mid-decision and strand focus on the body. The error waits for the dialog to close.
  if (status === "error" && !overlayOpen && !staffPanelOpen && (data === null || !calendarMode)) {
    return (
      <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
        {error ?? "Could not load the schedule week."} <button className="builder__navbtn" onClick={reload}>Retry</button>
      </p>
    );
  }

  const grid = buildWeekGrid(data ?? [], weekStart, timeZone);
  const weekLabel = `${days[0]} to ${days[6]}`;

  const browseDate = (date: string) => {
    navigation.browse(navigation.view, date);
    setCalendarTarget(null);
    setEmptyDate(null);
  };
  const selectDate = (date: string) => {
    browseDate(date);
    setCalendarTarget(null);
    setEmptyDate(date);
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };
  const calendarAction = (action: "assign" | "replace" | "revise" | "cancel") => {
    if (!canManage || status === "error" || calendarTarget === null) return;
    const target = toAssignTarget(calendarTarget.siteName, calendarTarget.routeName, calendarTarget.slot);
    setCalendarTarget(null);
    if (action === "assign") setAssignTarget(target);
    if (action === "replace") setReplaceTarget(target);
    if (action === "revise") setReviseTarget(target);
    if (action === "cancel") setCancelTarget(target);
  };

  return (
    <div className={`builder${calendarMode ? " manager-calendar-builder" : ""}`}>
      <div className="builder__bar">
        <div className="builder__nav" role="group" aria-label="Week navigation">
          <button type="button" className="builder__navbtn" onClick={() => browseDate(shiftWeek(weekStart, -1))}>
            Previous
          </button>
          <button
            type="button"
            className="builder__navbtn"
            onClick={() => browseDate(todayInZone(timeZone))}
          >
            Today
          </button>
          <button type="button" className="builder__navbtn" onClick={() => browseDate(shiftWeek(weekStart, 1))}>
            Next
          </button>
          <span className="builder__week">{weekLabel}</span>
          {calendarMode ? <label className="manager-calendar-date">Go to date<input type="date" value={selectedDate} onChange={(event) => { if (event.target.value) browseDate(event.target.value); }} /></label> : null}
        </div>
        <p className="builder__summary" role="status">
          {calendarMode ? data === null ? status === "error" ? "Selected week unavailable" : "Loading selected week…" : `${status === "error" ? "Last loaded" : "Loaded"} week: ` : ""}
          {(!calendarMode || data !== null) ? <>{grid.summary.totalSlots} slot{grid.summary.totalSlots === 1 ? "" : "s"}
          {grid.summary.openSlots > 0 ? ` - ${grid.summary.openSlots} OPEN` : ""}
          {grid.summary.atRiskSlots > 0 ? ` - ${grid.summary.atRiskSlots} at risk` : ""}</> : null}
        </p>
        <div className="builder__actions">
          {canManage ? (
            <button type="button" className="builder__navbtn" onClick={() => setCopyOpen(true)}>
              Copy last week
            </button>
          ) : (
            <button type="button" className="builder__pending" disabled title="Requires schedule manage permission">
              Copy last week
            </button>
          )}
          {canManage ? (
            <button type="button" className={calendarMode ? "builder__navbtn" : "minter__confirm-go"} onClick={() => setPublishOpen(true)}>
              {calendarMode ? "Publish patterns" : "Publish"}
            </button>
          ) : (
            <button type="button" className="builder__pending" disabled title="Requires schedule manage permission">
              {calendarMode ? "Publish patterns" : "Publish"}
            </button>
          )}
          {/* Wave 2 board rule 7: Builder is the power room. Publish stays the one loud
              control here and week cancel moves into this overflow, confirm-gated exactly
              as the 2026-08-15 lock wrote it. Without schedule.manage the menu is empty
              and renders nothing, so the surface keeps failing closed. */}
          <RowMenu
            ariaLabel="More week actions"
            triggerRef={weekActionsTriggerRef}
            items={rowMenuItems([
              canManage
                ? {
                    key: "cancel-week",
                    label: "Cancel remaining slots this week",
                    destructive: true,
                    help: "Confirm-gated. Cancelled times still block republish of the same time.",
                    onSelect: () => setBulkCancelOpen(true),
                  }
                : null,
            ])}
          />
        </div>
      </div>
      {status === "error" ? <p role="alert" tabIndex={-1} className="surface__state surface__state--error">{error ?? "Could not reload the schedule week."} The open panel is retained; reload the week before starting another change. <button className="builder__navbtn" onClick={reload}>Reload week</button></p> : null}
      {calendarMode ? (
        <>
          {(data?.length ?? 0) >= 500 ? <p role="status" className="surface__state">Showing the first 500 shifts in this week. This calendar may not include every shift.</p> : null}
          <CoverageViews view={navigation.view} onViewChange={view => navigation.browse(view, selectedDate)} grid={grid} recordsReady={data !== null} stale={status === "error"}
            staff={<StaffWeekSchedule key={weekStart} client={client} accountId={accountId} days={days} timeZone={timeZone} selectedDate={selectedDate} canManage={canManage && status !== "error"} pollMs={pollMs} coverage={coverageSnapshot?.rows ?? []} onPanelOpenChange={setStaffPanelOpen} onBrowseDate={browseDate} onAssigned={reload} onSelectSlot={(slot, siteName, routeName, trigger, fillMode) => { returnFocus.current = trigger ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null); setEmptyDate(null); setCalendarTarget({ slot, siteName, routeName, fillMode }); }} />}
            renderSite={visibleGrid => <ManagerCalendar grid={visibleGrid} timeZone={timeZone} selectedDate={selectedDate} onBrowseDate={browseDate} onSelectDate={selectDate} onSelectSlot={(slot, siteName, routeName) => {
            returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            setEmptyDate(null); setCalendarTarget({ slot, siteName, routeName });
          }} />} />
        </>
      ) : grid.posts.length === 0 ? (
        <p className="surface__state" role="status">
          No shift slots this week yet. Create a template in the Templates panel below, then use
          Publish to fill this week - the grid appears as soon as slots exist.
        </p>
      ) : (
        <div className="builder__scroll" tabIndex={0} aria-label="Week grid (scroll horizontally on smaller screens)">
          <table className="builder__grid">
            <thead>
              <tr>
                <th scope="col" className="builder__post-col">
                  Post
                </th>
                {grid.days.map((date) => (
                  <th key={date} scope="col" className="builder__day-col">
                    {formatDayHeader(date, timeZone)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.posts.map((post) => (
                <tr key={`${post.siteName}-${post.routeName}`}>
                  <th scope="row" className="builder__post">
                    <span className="builder__site">{post.siteName}</span>
                    <span className="builder__route">{post.routeName}</span>
                  </th>
                  {post.days.map((day) => (
                    <td key={day.date} className="builder__cell">
                      {day.slots.map((slot) => (
                        <SlotCard
                          key={slot.id}
                          slot={slot}
                          canManage={canManage}
                          onAssign={() =>
                            setAssignTarget(toAssignTarget(post.siteName, post.routeName, slot))
                          }
                          onCancel={() =>
                            setCancelTarget(toAssignTarget(post.siteName, post.routeName, slot))
                          }
                          onReplace={() =>
                            setReplaceTarget(toAssignTarget(post.siteName, post.routeName, slot))
                          }
                          onRevise={() =>
                            setReviseTarget(toAssignTarget(post.siteName, post.routeName, slot))
                          }
                        />
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {calendarMode && calendarTarget !== null ? (
        <ScheduleOverlay sidePanel onClose={() => setCalendarTarget(null)} returnFocusRef={returnFocus}>
          <CalendarShiftPanel client={client} accountId={accountId} slot={calendarTarget.slot} siteName={calendarTarget.siteName} routeName={calendarTarget.routeName} timeZone={timeZone} fillMode={calendarTarget.fillMode ?? data?.find(row => row.shiftSlotId === calendarTarget.slot.id)?.openFillMode ?? "instant_claim"} canManage={canManage && status !== "error"} onAction={calendarAction} onClose={() => setCalendarTarget(null)} />
        </ScheduleOverlay>
      ) : null}
      {calendarMode && emptyDate !== null ? (
        <ScheduleOverlay sidePanel onClose={() => setEmptyDate(null)} returnFocusRef={returnFocus}>
          <aside className="builder__drawer" role="dialog" aria-modal="true" aria-label="Schedule a shift">
            <div className="builder__drawer-head"><h3>Schedule a shift</h3><button className="builder__navbtn" onClick={() => setEmptyDate(null)}>Close</button></div>
            <label className="manager-calendar-date">Selected date<input type="date" value={emptyDate} onChange={(event) => { if (event.target.value) { setEmptyDate(event.target.value); navigation.browse(navigation.view, event.target.value, true); } }} /></label>
            <p>Define a shift pattern, choose this service week, and review the shifts before scheduling. Then select a scheduled shift to assign officers directly.</p>
            <p>The pattern controls times and days. This step does not create and assign a shift in one transaction.</p>
            {canManage && onDefineShift ? <button className="minter__confirm-go" onClick={() => { const date = emptyDate; setEmptyDate(null); onDefineShift(date); }}>Define shift pattern</button> : <p>Schedule changes are unavailable right now. Check your access or try again.</p>}
          </aside>
        </ScheduleOverlay>
      ) : null}
      {canManage && assignTarget !== null ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setAssignTarget(null)}>
          <AssignDrawer
            client={client}
            accountId={accountId}
            target={assignTarget}
            onClose={() => setAssignTarget(null)}
            onAssigned={reload}
          />
        </ScheduleOverlay>
      ) : null}
      {canManage && copyOpen ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setCopyOpen(false)}>
          <CopyWeekDialog
            client={client}
            accountId={accountId}
            timeZone={timeZone}
            targetWeekStart={weekStart}
            targetRows={data ?? []}
            onClose={() => setCopyOpen(false)}
            onApplied={reload}
          />
        </ScheduleOverlay>
      ) : null}
      {canManage ? (
        <BulkCancelWeekControl
          client={client}
          accountId={accountId}
          windowStart={days[0]!}
          windowEnd={days[6]!}
          coverage={data ?? []}
          onCancelled={reload}
          open={bulkCancelOpen}
          onOpenChange={setBulkCancelOpen}
          returnFocusRef={weekActionsTriggerRef}
        />
      ) : null}
      {canManage && publishOpen ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setPublishOpen(false)}>
          <PublishDialog
            calendarMode={calendarMode}
            client={client}
            accountId={accountId}
            windowStart={days[0]!}
            windowEnd={days[6]!}
            coverage={data ?? []}
            onClose={() => setPublishOpen(false)}
            onPublished={reload}
          />
        </ScheduleOverlay>
      ) : null}
      {canManage && cancelTarget !== null ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setCancelTarget(null)}>
          <CancelDialog
            client={client}
            accountId={accountId}
            target={cancelTarget}
            onClose={() => setCancelTarget(null)}
            onCancelled={reload}
          />
        </ScheduleOverlay>
      ) : null}
      {canManage && replaceTarget !== null ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setReplaceTarget(null)}>
          <ReplaceDrawer
            client={client}
            accountId={accountId}
            target={replaceTarget}
            onClose={() => setReplaceTarget(null)}
            onReplaced={reload}
          />
        </ScheduleOverlay>
      ) : null}
      {canManage && reviseTarget !== null ? (
        <ScheduleOverlay sidePanel={calendarMode} returnFocusRef={returnFocus} onClose={() => setReviseTarget(null)}>
          <ReviseBoundsDialog
            client={client}
            accountId={accountId}
            target={reviseTarget}
            onClose={() => setReviseTarget(null)}
            onRevised={reload}
          />
        </ScheduleOverlay>
      ) : null}
    </div>
  );
}

/**
 * One selected template + the exact visible week. The read-only picker loads on open; the
 * destructive RPC remains at zero calls until the final AlertDialog action. Radix owns the
 * alertdialog role, accessible name/description, focus trap/return, Escape, portal, and
 * background scroll lock rather than this surface approximating any of those contracts.
 */
function BulkCancelWeekControl({
  client,
  accountId,
  windowStart,
  windowEnd,
  coverage,
  onCancelled,
  open,
  onOpenChange: setOpen,
  returnFocusRef,
}: {
  client: SupabaseClient;
  accountId: string;
  windowStart: string;
  windowEnd: string;
  coverage: readonly ScheduleCoverageRow[];
  onCancelled: () => void;
  /**
   * Wave 2 board rule 6: week cancel lost its headline button and now opens from the
   * Builder's overflow menu. The dialog, its confirm, its refusals, and its
   * block-republish helper are untouched; only the address moved, so the parent owns the
   * open state while this control keeps every semantic it shipped with.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Guaranteed focus target on close: the overflow trigger that outlives the menu item. */
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}) {
  const [templates, setTemplates] = useState<readonly ShiftTemplateRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<CancelTemplateWeekOutcome | null>(null);

  // Whatever had focus when the dialog opened (the overflow menu trigger in practice),
  // so close can hand focus back to a control that is still on screen.
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement ? active : null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setTemplates(null);
    setLoadError(null);
    setSelectedId(null);
    setOutcome(null);
    fetchShiftTemplates(client, accountId)
      .then((load) => {
        if (active) setTemplates(load.templates);
      })
      .catch(() => {
        if (active) setLoadError("Could not load shift templates.");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, open]);

  const weekTemplateIds = new Set(
    coverage.flatMap((row) => (row.shiftTemplateId === null ? [] : [row.shiftTemplateId])),
  );
  const candidates = templates?.filter((template) => weekTemplateIds.has(template.templateId)) ?? [];
  const selected = candidates.find((template) => template.templateId === selectedId) ?? null;
  const selectedRows =
    selected === null
      ? []
      : coverage.filter((row) => row.shiftTemplateId === selected.templateId);
  const remainingCount = selectedRows.filter((row) => row.slotStatus !== "cancelled").length;
  const alreadyCancelledCount = selectedRows.length - remainingCount;
  const succeeded = outcome !== null && outcome.ok;
  const canConfirm = selected !== null && remainingCount > 0 && !busy && !succeeded;
  const confirmTitle =
    selected === null
      ? "Choose a template first"
      : remainingCount === 0
        ? "No non-cancelled slots remain for this template in the visible week"
        : undefined;

  async function confirmCancel(): Promise<void> {
    if (!canConfirm || selected === null) return;
    setBusy(true);
    setOutcome(null);
    try {
      const result = await cancelShiftTemplateWeek(
        client,
        accountId,
        selected.templateId,
        windowStart,
        windowEnd,
      );
      setOutcome(result);
      if (result.ok) onCancelled();
    } catch {
      setOutcome({ ok: false, reason: SAFE_TEMPLATE_WEEK_CANCEL_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="glass-modal schedule__alert-overlay" />
        <AlertDialog.Content
          className="glass-modal__panel glass-modal__panel--sm schedule__alert-content"
          /*
           * Radix restores focus to its own Trigger on close. This dialog no longer has
           * one (the verb moved into the Builder overflow menu, board rule 7), so without
           * this handler focus would fall to the body and strand a keyboard operator.
           * Returning it to whatever opened the dialog keeps the pre-Wave-2 behavior with
           * the new address.
           */
          onCloseAutoFocus={(event) => {
            // Prefer whatever opened the dialog, but never simply give up: Radix's own
            // handler would then focus a Trigger that no longer exists and leave the
            // operator on the body. The menu trigger is the guaranteed fallback because
            // it outlives the menu item that was clicked.
            const opener = openerRef.current;
            const target =
              opener !== null && document.body.contains(opener) ? opener : returnFocusRef.current;
            if (target === null || !document.body.contains(target)) return;
            event.preventDefault();
            target.focus();
          }}
        >
          <span className="glass-modal__shine" aria-hidden="true" />
          <div className="glass-modal__body">
            <div className="minter__confirm">
              <AlertDialog.Title className="minter__confirm-title">
                Cancel remaining slots this week?
              </AlertDialog.Title>
              <AlertDialog.Description className="minter__confirm-note">
                Choose one template for the visible week {windowStart} to {windowEnd}. Every
                non-cancelled slot from that template in this exact week will be cancelled.
              </AlertDialog.Description>
              <p className="minter__confirm-warning">
                Cancelled times still block republish of the same time.
              </p>

              {loadError !== null ? (
                <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
                  {loadError}
                </p>
              ) : templates === null ? (
                <p className="surface__state" role="status">
                  Loading templates...
                </p>
              ) : candidates.length === 0 ? (
                <p className="surface__state" role="status">
                  No current template has slots in this visible week.
                </p>
              ) : (
                <fieldset className="minter__type" disabled={busy || succeeded}>
                  <legend className="minter__type-legend">Template in this week</legend>
                  {candidates.map((template) => {
                    const templateRows = coverage.filter(
                      (row) => row.shiftTemplateId === template.templateId,
                    );
                    const templateRemaining = templateRows.filter(
                      (row) => row.slotStatus !== "cancelled",
                    ).length;
                    return (
                      <label key={template.templateId} className="minter__type-option">
                        <input
                          type="radio"
                          name="bulk-cancel-week-template"
                          value={template.templateId}
                          checked={selectedId === template.templateId}
                          onChange={() => {
                            setSelectedId(template.templateId);
                            setOutcome(null);
                          }}
                        />
                        <span>
                          <b>{template.name}</b> — {templateRemaining} remaining, {templateRows.length - templateRemaining}{" "}
                          already cancelled
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              )}

              {selected !== null ? (
                <p className="builder__drawer-slot">
                  Selected: {selected.name}
                  <span className="builder__window">
                    {windowStart} to {windowEnd}
                  </span>
                  <span className="builder__fraction">
                    {remainingCount} remaining — {alreadyCancelledCount} already cancelled
                  </span>
                </p>
              ) : null}

              {outcome !== null ? (
                outcome.ok ? (
                  <p className="builder__drawer-ok" role="status">
                    Cancelled {outcome.cancelledSlotCount} slot
                    {outcome.cancelledSlotCount === 1 ? "" : "s"}. {outcome.alreadyCancelledSlotCount}{" "}
                    already cancelled; {outcome.cancelledAssignmentCount} assignment
                    {outcome.cancelledAssignmentCount === 1 ? "" : "s"} cancelled.
                  </p>
                ) : (
                  <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
                    {outcome.reason}
                  </p>
                )
              ) : null}

              <div className="minter__confirm-actions">
                <AlertDialog.Cancel asChild>
                  <button type="button" className="builder__navbtn" disabled={busy}>
                    {succeeded ? "Close" : "Keep slots"}
                  </button>
                </AlertDialog.Cancel>
                {!succeeded ? (
                  <AlertDialog.Action asChild>
                    <button
                      type="button"
                      className="minter__confirm-go minter__confirm-go--danger"
                      disabled={!canConfirm}
                      title={confirmTitle}
                      onClick={(event) => {
                        event.preventDefault();
                        void confirmCancel();
                      }}
                    >
                      {busy ? "Cancelling..." : "Confirm cancellation"}
                    </button>
                  </AlertDialog.Action>
                ) : null}
              </div>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/**
 * The publish review gate. Publishing is the moment a draft becomes something officers act
 * on, so this is a review rather than a naked confirm: pick a template, read exactly what the
 * publish will do to the visible week, then commit.
 *
 * Lane 5 (connected flow, lock O-4) moved the CLAIM and the ACT to the host. The counts an
 * operator commits against, and the single reason a publish cannot run, come from
 * `management_schedule_publish_review` through the shared gate, which is the same review the
 * publish act re-checks. Before this, the wizard and this dialog each derived their own
 * answer from their own client-side mirror of the expansion, which is how two screens end up
 * disagreeing about the same week.
 *
 * The per-day table below is still built from coverage rows the Builder already holds. It
 * stays as detail the operator can read row by row, and it no longer states the number that
 * gates the button.
 *
 * Reads on open: the template picker list and one publish review per (template, week). The
 * only write is `publish_shift_template_week`, called once, on confirm, against the revision
 * the review issued.
 *
 * What this gate deliberately does NOT show: the design mock's assignment-level "changed" and
 * "removed" rows. This publish path creates slots and never edits or removes them, so those
 * groups would be decoration standing in for facts. The residual is stated in the dialog
 * rather than mocked up.
 */
function PublishDialog({
  calendarMode = false,
  client,
  accountId,
  windowStart,
  windowEnd,
  coverage,
  onClose,
  onPublished,
}: {
  calendarMode?: boolean;
  client: SupabaseClient;
  accountId: string;
  windowStart: string;
  windowEnd: string;
  /** The visible week's rows, already loaded by the Builder. Keeps this gate zero-rpc. */
  coverage: readonly ScheduleCoverageRow[];
  onClose: () => void;
  onPublished: () => void;
}) {
  const [templates, setTemplates] = useState<readonly ShiftTemplateRow[] | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useScheduleBusy();
  const [receipt, setReceipt] = useState<SchedulePublishReceipt | null>(null);
  const [publishFailure, setPublishFailure] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  // The host review for the selected pattern and this week. Keyed so an answer about the
  // previous selection can never be rendered as this one's.
  const [review, setReview] = useState<
    { readonly key: string; readonly facts: SchedulePublishReviewFacts } | null
  >(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewNonce, setReviewNonce] = useState(0);
  // A pre-6A server has no color_token or open_fill_mode, so the chip and the fill-mode
  // sentences below would be client defaults wearing the authority of read data.
  const [colorsDegraded, setColorsDegraded] = useState(false);

  useEffect(() => {
    let active = true;
    fetchShiftTemplates(client, accountId)
      .then((loaded) => {
        if (!active) return;
        setTemplates(loaded.templates);
        setColorsDegraded(loaded.degraded);
      })
      .catch((caught: unknown) => {
        if (active) setPickerError(caught instanceof Error ? caught.message : "Could not load shift templates.");
      });
    return () => {
      active = false;
    };
  }, [client, accountId]);

  const selected = templates?.find((template) => template.templateId === selectedId) ?? null;
  const preview =
    selected !== null ? buildPublishPreview(selected, coverage, windowStart, windowEnd) : null;

  // One review per (pattern, week), including the no-pattern-yet case: the host answers that
  // with its own blocker, so the zero state is a read rather than a special case this
  // surface invents. Skipped once a publish has landed; the receipt is the truth then.
  const reviewKey = `${selectedId ?? ""}:${windowStart}`;
  useEffect(() => {
    if (templates === null || receipt !== null) {
      return;
    }
    let active = true;
    setReviewError(null);
    void fetchSchedulePublishReview(client, accountId, selectedId, windowStart).then((outcome) => {
      if (!active) return;
      if (outcome.ok) {
        setReview({ key: reviewKey, facts: outcome.review });
      } else {
        setReview(null);
        setReviewError(outcome.reason);
      }
    });
    return () => {
      active = false;
    };
    // reviewNonce re-runs the read after a refused or conflicted act, so the dialog states
    // the current answer rather than the one the operator already acted on.
  }, [client, accountId, selectedId, windowStart, templates, receipt, reviewKey, reviewNonce]);

  const reviewFacts = review !== null && review.key === reviewKey ? review.facts : null;
  const gate = resolveSchedulePublishGate({
    // The dialog only mounts under schedule.manage; the host re-checks regardless.
    canManageSchedule: true,
    review: reviewFacts,
    reviewLoading: reviewError === null,
    reviewError,
  });

  async function confirmPublish(): Promise<void> {
    if (busy || selectedId === null || gate.reviewRevision === null) return;
    setBusy(true);
    setPublishFailure(null);
    try {
      const result = await publishShiftTemplateWeek(
        client,
        accountId,
        selectedId,
        windowStart,
        gate.reviewRevision,
      );
      if (result.ok) {
        // This device's clock at the moment the write was confirmed. The receipt carries no
        // timestamp, so this must not dress it up as the server's own record: the audit row
        // is the record, and it is written server-side.
        setPublishedAt(new Date().toISOString());
        setReceipt(result.receipt);
        onPublished();
        return;
      }
      if (result.kind === "conflict") {
        setPublishFailure(result.reason);
      } else if (result.kind === "unavailable") {
        setPublishFailure(result.reason);
      }
      // Refused and conflicted both mean nothing was written and the review the operator
      // read is stale. Re-read so the dialog states the host's current reason itself.
      setReview(null);
      setReviewNonce((nonce) => nonce + 1);
    } catch {
      setPublishFailure(SAFE_PUBLISH_WEEK_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  const published = receipt !== null;
  return (
    <div className="minter__confirm pubrev" role="dialog" aria-modal="true" aria-label={`Publish review - ${windowStart} to ${windowEnd}`}>
      <h3 className="minter__confirm-title">Review before publish</h3>
      <PublishFlow stage={published ? "published" : "review"} />

      {/* Pre-publish the shared gate states the week, so it is not said twice. */}
      {published ? (
        <>
          <p className="pubrev__week">{windowStart} to {windowEnd}</p>
          <PublishReceipt receipt={receipt} publishedAt={publishedAt} />
        </>
      ) : (
        <>
          {pickerError !== null ? (
            <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>{pickerError}</p>
          ) : templates === null ? (
            <p className="surface__state" role="status">Loading templates...</p>
          ) : templates.length === 0 ? (
            <p className="surface__state" role="status">
              No publishable templates yet. Create one in Templates, then publish it here.
            </p>
          ) : (
            <SchedulePublishReview
              gate={gate}
              weekLine={`${windowStart} to ${windowEnd}`}
              busy={busy}
              onPublish={() => void confirmPublish()}
            >
              <ul className="builder__drawer-guards" aria-label="Publishable templates">
                {templates.map((template) => (
                  <li key={template.templateId}>
                    <label className="minter__type-option">
                      <input
                        type="radio"
                        name="publish-template"
                        checked={selectedId === template.templateId}
                        onChange={() => setSelectedId(template.templateId)}
                      />
                      {colorsDegraded ? null : (
                        <TemplateColorChip colorToken={template.colorToken} />
                      )}
                      {template.name}
                      {colorsDegraded ? null : (
                        <span className="builder__fraction">
                          {OPEN_FILL_MODE_COPY[template.openFillMode]}
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
              {colorsDegraded ? (
                <p className="surface__state" role="status">
                  {SCHEDULE_COLORS_UNAVAILABLE} Every template below reads as slate and instant
                  claim because this server does not report them yet.
                </p>
              ) : null}
              {selected !== null && preview !== null ? (
                <PublishPreviewTable
                  template={selected}
                  preview={preview}
                  colorsDegraded={colorsDegraded}
                />
              ) : null}
            </SchedulePublishReview>
          )}
          {publishFailure !== null ? (
            <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>{publishFailure}</p>
          ) : null}
        </>
      )}

      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          {published ? (calendarMode ? "Back to Calendar" : "Back to builder") : "Keep editing"}
        </button>
      </div>
    </div>
  );
}

/** Draft, Review, Published. Visual only: template status stays the underlying truth. */
function PublishFlow({ stage }: { stage: "review" | "published" }) {
  const steps = [
    { key: "draft", name: "Draft", sub: "only supervisors see it" },
    { key: "review", name: "Review", sub: stage === "review" ? "you are here" : "done" },
    { key: "published", name: "Published", sub: "live to officers" },
  ] as const;
  return (
    <ol className="pubrev__flow" aria-label="Publish flow">
      {steps.map((step) => (
        <li
          key={step.key}
          className={`pubrev__step${step.key === stage ? " pubrev__step--here" : ""}`}
          aria-current={step.key === stage ? "step" : undefined}
        >
          <span className="pubrev__step-name">{step.name}</span>
          <span className="pubrev__step-sub">{step.sub}</span>
        </li>
      ))}
    </ol>
  );
}

/** Exactly what the publish will do, row by row, plus the consequences it can prove. */
function PublishPreviewTable({
  template,
  preview,
  colorsDegraded,
}: {
  template: ShiftTemplateRow;
  preview: ReturnType<typeof buildPublishPreview>;
  /** Suppresses the fill-mode consequence, which is a client default on a pre-6A server. */
  colorsDegraded: boolean;
}) {
  const { rows, summary } = preview;
  if (preview.clippedToNothing) {
    return (
      <p className="surface__state" role="status">
        This template is not active during this week, so publishing it here would do nothing.
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        This template has no matching days in this week. Nothing would be created.
      </p>
    );
  }
  return (
    <div className="pubrev__preview">
      <p className="pubrev__counts" role="status">
        {summary.createCount} to create, {summary.existsCount} already there.
      </p>
      <table className="pubrev__table">
        <caption className="pubrev__caption">
          What publishing {template.name} does to this week
        </caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col">Window</th>
            <th scope="col">Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <td>
                {row.weekdayLabel} {row.date.slice(5)}
              </td>
              <td className="schedule__window">{row.window}</td>
              <td>
                {row.verdict === "create" ? (
                  <>
                    <span className="pubrev__verdict pubrev__verdict--create">Creates a slot</span>
                    {row.addsAlongsideExisting ? (
                      <span className="pubrev__note">
                        A slot already sits on this day at another time. This adds a second one
                        beside it.
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <span className="pubrev__verdict">Already there</span>
                    {row.existingIsCancelled ? (
                      <span className="pubrev__note">
                        That slot is cancelled. Publishing does not bring it back.
                      </span>
                    ) : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="pubrev__warns">
        {summary.openSeatCount > 0 ? (
          <li>
            <b>
              {summary.openSeatCount} open seat{summary.openSeatCount === 1 ? "" : "s"} go live.
            </b>{" "}
            {colorsDegraded
              ? "This server does not report how open seats fill, so how they behave is not shown here."
              : template.openFillMode === "request_approval"
                ? "Approval required means these seats do not fill themselves. A supervisor approves each request."
                : "Instant claim means an officer can take one when it appears."}
          </li>
        ) : null}
        {summary.cancelledBlockingCount > 0 ? (
          <li>
            <b>
              {summary.cancelledBlockingCount} cancelled slot
              {summary.cancelledBlockingCount === 1 ? "" : "s"} stay cancelled.
            </b>{" "}
            Publishing skips a day that already has a slot at this time, even a cancelled one.
          </li>
        ) : null}
        {summary.duplicateTimeCount > 0 ? (
          <li>
            <b>
              {summary.duplicateTimeCount} day{summary.duplicateTimeCount === 1 ? "" : "s"} get a
              second slot.
            </b>{" "}
            The template start time no longer matches the slot already there, so this adds one
            rather than moving it.
          </li>
        ) : null}
        <li>
          Officers are notified once, at publish, where app notifications are switched on.
          Drafts never notify anyone.
        </li>
        <li className="pubrev__scope">
          This publish only creates slots. It does not move or remove anything already
          scheduled, and it does not change assignments.
        </li>
      </ul>
    </div>
  );
}

/**
 * Proof green belongs to a recorded write, so this renders only after one returned ok, and
 * every count comes from the server's own response rather than from the client's projection.
 * The time is this device's clock and says so - the RPC returns no timestamp to quote.
 */
function PublishReceipt({
  receipt,
  publishedAt,
}: {
  receipt: SchedulePublishReceipt;
  publishedAt: string | null;
}) {
  const carried = receipt.weekSlotCount - receipt.createdSlotCount;
  return (
    <div className="pubrev__receipt">
      <p className="builder__drawer-ok" role="status">
        {receipt.createdSlotCount > 0
          ? "Published and live to officers."
          : "Nothing new was created. This week was already published."}
      </p>
      {/* The server's own receipt, repeated. Services and physical obligation seats are
          separate facts and never multiplied together. */}
      <p className="pubrev__receipt-line">
        {buildPublishReceiptLine(receipt)}
        {carried > 0 ? ` ${carried} already existed.` : ""}
        {publishedAt !== null ? ` Confirmed at ${publishedAt.slice(11, 19)} UTC on this device.` : ""}
      </p>
    </div>
  );
}

/**
 * Copy-last-week preview + LIVE APPLY. The preview (previous week via the existing coverage
 * range read, compared against the visible week) is the confirmation context; clicking
 * Apply is the explicit confirmation and runs Codex's copy_shift_week exactly once
 * (published source slots only, assignments never copy, idempotent server-side). Receipt
 * counts render before the visible week refetches behind them.
 */
function CopyWeekDialog({
  client,
  accountId,
  timeZone,
  targetWeekStart,
  targetRows,
  onClose,
  onApplied,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  targetWeekStart: string;
  targetRows: ScheduleCoverageRow[];
  onClose: () => void;
  onApplied: () => void;
}) {
  const [preview, setPreview] = useState<CopyWeekPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<CopyWeekOutcome | null>(null);
  const sourceWeekStart = shiftWeek(targetWeekStart, -1);

  async function confirmApply(): Promise<void> {
    if (busy || preview === null || preview.rows.length === 0) return;
    setBusy(true);
    try {
      const result = await copyShiftWeek(client, accountId, sourceWeekStart, targetWeekStart);
      setOutcome(result);
      if (result.ok) {
        onApplied();
      }
    } catch {
      setOutcome({ ok: false, reason: SAFE_COPY_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let active = true;
    const sourceDays = weekDays(sourceWeekStart);
    fetchScheduleCoverageRange(client, accountId, sourceDays[0]!, sourceDays[6]!)
      .then((sourceRows) => {
        if (active) {
          setPreview(buildCopyWeekPreview(sourceRows, targetRows, sourceWeekStart, targetWeekStart, timeZone));
        }
      })
      .catch((caught: unknown) => {
        if (active) setLoadError(caught instanceof Error ? caught.message : "Could not load the schedule week.");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, sourceWeekStart, targetWeekStart, timeZone, targetRows]);

  return (
    <div className="minter__confirm" role="dialog" aria-modal="true" aria-label={`Copy last week - into ${targetWeekStart}`}>
      <h3 className="minter__confirm-title">Copy last week - {sourceWeekStart} into {targetWeekStart}</h3>
      <p className="minter__confirm-note">
        Preview only: what a copy of last week's published slots would do to the visible week.
        Cancelled and draft slots are never copied; slots that already exist are left alone.
      </p>
      {loadError !== null ? (
        <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
          {loadError}
        </p>
      ) : preview === null ? (
        <p className="surface__state" role="status">
          Loading last week...
        </p>
      ) : preview.rows.length === 0 ? (
        <p className="surface__state" role="status">
          No slots last week - nothing to copy.
        </p>
      ) : (
        <>
          <p className="builder__summary" role="status">
            {preview.summary.copyCount} would copy - {preview.summary.existsCount} already exist -{" "}
            {preview.summary.skippedCount} skipped
          </p>
          <ul className="copyweek__rows">
            {preview.rows.map((row) => (
              <li key={`${row.siteName}-${row.routeName}-${row.targetDate}-${row.window}`} className="copyweek__row">
                <span className="copyweek__post">
                  {row.siteName} - {row.routeName}
                </span>
                <span className="builder__window">
                  {row.targetDate} {row.window}
                </span>
                <span className="builder__fraction">
                  {row.headcountRequired} guard{row.headcountRequired === 1 ? "" : "s"}
                </span>
                <span className={`schedule__chip schedule__chip--${row.verdict === "copy" ? "filled" : row.verdict === "exists" ? "partial" : "draft"}`}>
                  {row.verdict === "copy" ? "Would copy" : row.verdict === "exists" ? "Already exists" : `Skipped (${row.skippedReason})`}
                </span>
                {row.atRiskSource ? (
                  <span className="schedule__chip schedule__chip--at-risk">At risk last week</span>
                ) : null}
                {row.credentials.map((credential) => (
                  <span key={credential} className="schedule__chip schedule__chip--credential">
                    {humanizeLabel(credential)}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </>
      )}
      {outcome !== null ? (
        outcome.ok ? (
          <p className="builder__drawer-ok" role="status">
            Copied {outcome.copiedSlotCount} slot{outcome.copiedSlotCount === 1 ? "" : "s"}
            {outcome.existingSlotCount > 0 ? ` - ${outcome.existingSlotCount} already existed` : ""}
            {outcome.skippedSlotCount > 0 ? ` - ${outcome.skippedSlotCount} skipped` : ""} (of{" "}
            {outcome.totalSourceSlotCount} last week). Assignments never copy - staff the new slots.
          </p>
        ) : (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {outcome.reason}
          </p>
        )
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
        {outcome === null || !outcome.ok ? (
          preview !== null && preview.rows.length > 0 ? (
            <button type="button" className="minter__confirm-go" onClick={() => void confirmApply()} disabled={busy}>
              Apply copy
            </button>
          ) : (
            <button type="button" className="builder__pending" disabled title="Nothing to copy from last week">
              Apply copy
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}

/** Cancel a published slot behind explicit confirmation; refetch behind the receipt. */
function CancelDialog({
  client,
  accountId,
  target,
  onClose,
  onCancelled,
}: {
  client: SupabaseClient;
  accountId: string;
  target: AssignTarget;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<{ ok: true; count: number } | { ok: false; reason: string } | null>(null);

  async function confirmCancel(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const result = await cancelShiftSlot(client, accountId, target.slotId);
      if (result.ok) {
        setOutcome({ ok: true, count: result.cancelledAssignmentCount });
        onCancelled();
      } else {
        setOutcome({ ok: false, reason: result.reason });
      }
    } catch {
      setOutcome({ ok: false, reason: SAFE_CANCEL_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="minter__confirm" role="dialog" aria-modal="true" aria-label={`Cancel slot - ${target.siteName}, ${target.routeName}`}>
      <h3 className="minter__confirm-title">Cancel slot - {target.routeName}</h3>
      <p className="builder__drawer-slot">
        {target.siteName}
        <span className="builder__window">{target.window}</span>
        <span className="builder__fraction">{target.fraction}</span>
      </p>
      <p className="minter__confirm-warning">
        Scheduled members on this slot are unassigned. The slot stays visible as cancelled.
        Slots with started or completed work cannot be cancelled.
        Cancelled times still block republish of the same time.
      </p>
      {outcome !== null ? (
        outcome.ok ? (
          <p className="builder__drawer-ok" role="status">
            Slot cancelled{outcome.count > 0 ? ` - ${outcome.count} assignment${outcome.count === 1 ? "" : "s"} unassigned` : ""}.
          </p>
        ) : (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {outcome.reason}
          </p>
        )
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
        {outcome === null || !outcome.ok ? (
          <button type="button" className="minter__confirm-go" onClick={() => void confirmCancel()} disabled={busy}>
            Confirm cancel
          </button>
        ) : null}
      </div>
    </div>
  );
}


function toAssignTarget(siteName: string, routeName: string, slot: BuilderSlot): AssignTarget {
  return {
    slotId: slot.id,
    siteName,
    routeName,
    window: slot.window,
    fraction: slot.fraction,
    startsAt: slot.startsAt,
    endsAt: slot.endsAt,
    headcountRequired: slot.headcountRequired,
  };
}

function seatFillLabel(seat: ScheduleLineageSeat): string {
  if (seat.fillState === "cancelled") {
    return "Cancelled after publish";
  }
  if (seat.fillState === "open") {
    return "Open";
  }
  return seat.memberLabel ?? "Assigned";
}

function ReplaceDrawer({
  client,
  accountId,
  target,
  onClose,
  onReplaced,
}: {
  client: SupabaseClient;
  accountId: string;
  target: AssignTarget;
  onClose: () => void;
  onReplaced: () => void;
}) {
  const [lineage, setLineage] = useState<ScheduleSlotLineage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [members, setMembers] = useState<AssignableMember[] | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<ReplaceSeatOutcome | null>(null);

  useEffect(() => {
    let active = true;
    setLineage(null);
    setLoadError(null);
    setMembers(null);
    setPickerError(null);
    setSelectedSeat(null);
    setOutcome(null);
    void fetchScheduleSlotLineage(client, accountId, target.slotId).then((result) => {
      if (!active) return;
      if (!result.ok) {
        setLoadError(result.reason);
        return;
      }
      setLineage(result.lineage);
      const replaceable = result.lineage.seats.filter((seat) => seat.hasOutgoingAssignment);
      if (replaceable.length === 1) {
        setSelectedSeat(replaceable[0]!.seatOrdinal);
      }
    });
    void fetchAssignableMembers(client, accountId)
      .then((loaded) => {
        if (active) setMembers(loaded);
      })
      .catch((caught: unknown) => {
        if (active) {
          setPickerError(caught instanceof Error ? caught.message : "Could not load the member picker.");
        }
      });
    return () => {
      active = false;
    };
  }, [client, accountId, target.slotId]);

  async function replace(member: AssignableMember, seatOrdinal: number, expectedRevision: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const result = await replaceScheduleSeatAssignment(
        client,
        accountId,
        target.slotId,
        seatOrdinal,
        expectedRevision,
        member.guardMembershipId,
      );
      setOutcome(result);
      if (result.ok) {
        onReplaced();
      }
    } catch {
      setOutcome({ ok: false, kind: "unavailable", reason: SAFE_REPLACE_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  const succeeded = outcome !== null && outcome.ok;
  const weekEnded = lineage?.weekEnded === true;

  return (
    <aside className="builder__drawer" role="dialog" aria-modal="true" aria-label={`Replace member - ${target.siteName}, ${target.routeName}`}>
      <div className="builder__drawer-head">
        <h3 className="builder__drawer-title">Replace member</h3>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
      <p className="builder__drawer-slot">
        {target.siteName} - {target.routeName}
        <span className="builder__window">{target.window}</span>
        <span className="builder__fraction">{target.fraction}</span>
      </p>
      {weekEnded ? (
        <p className="surface__state" role="status">
          This week has ended. Review it in <Link to="/schedule">Schedule</Link>,{" "}
          <Link to={scheduleTimesheetsPath()}>Timesheets</Link>.
        </p>
      ) : null}
      {loadError !== null ? (
        <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
          {loadError}
        </p>
      ) : lineage === null && !weekEnded ? (
        <p className="surface__state" role="status">
          Loading seat lineage...
        </p>
      ) : null}
      {outcome !== null ? (
        outcome.ok ? (
          <p className="builder__drawer-ok" role="status">
            {outcome.outcome === "handoff_recorded" ? "Handoff recorded" : "Member replaced"} on seat{" "}
            {outcome.seatOrdinal}.
          </p>
        ) : (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {outcome.reason === SCHEDULE_WEEK_ENDED ? (
              <>
                This week has ended. Review it in <Link to="/schedule">Schedule</Link>,{" "}
                <Link to={scheduleTimesheetsPath()}>Timesheets</Link>.
              </>
            ) : (
              outcome.reason
            )}
          </p>
        )
      ) : null}
      {!succeeded && lineage !== null && !weekEnded ? (
        <>
          <ul className="builder__drawer-guards">
            {lineage.seats.map((seat) => (
              <li key={seat.seatLocator}>
                <button
                  type="button"
                  className="builder__drawer-guard"
                  disabled={!seat.hasOutgoingAssignment || busy}
                  aria-pressed={selectedSeat === seat.seatOrdinal}
                  onClick={() => setSelectedSeat(seat.seatOrdinal)}
                >
                  <span className="builder__drawer-guard-label">
                    Seat {seat.seatOrdinal}: {seatFillLabel(seat)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {selectedSeat === null ? (
            <p className="surface__state" role="status">
              Choose a seat with an assigned member to replace.
            </p>
          ) : pickerError !== null ? (
            <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
              {pickerError}
            </p>
          ) : members === null ? (
            <p className="surface__state" role="status">
              Loading members...
            </p>
          ) : (
            <ul className="builder__drawer-guards">
              {members.map((member) => {
                const picked = resolveGuardName({
                  displayName: member.guardDisplayName,
                  label: member.guardLabel,
                  ref: member.guardRef,
                  fallback: "Member",
                });
                return (
                  <li key={member.guardMembershipId}>
                    <button
                      type="button"
                      className="builder__drawer-guard"
                      disabled={busy}
                      title={guardRefTitle(picked.ref)}
                      onClick={() =>
                        void replace(member, selectedSeat, lineage.expectedRevision)
                      }
                    >
                      <span className="builder__drawer-guard-label">{picked.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
    </aside>
  );
}

function ReviseBoundsDialog({
  client,
  accountId,
  target,
  onClose,
  onRevised,
}: {
  client: SupabaseClient;
  accountId: string;
  target: AssignTarget;
  onClose: () => void;
  onRevised: () => void;
}) {
  const [lineage, setLineage] = useState<ScheduleSlotLineage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [headcountInput, setHeadcountInput] = useState(String(target.headcountRequired));
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<ReviseBoundsOutcome | null>(null);

  useEffect(() => {
    let active = true;
    setLineage(null);
    setLoadError(null);
    setOutcome(null);
    setStartInput(toLocalInput(target.startsAt));
    setEndInput(toLocalInput(target.endsAt));
    setHeadcountInput(String(target.headcountRequired));
    void fetchScheduleSlotLineage(client, accountId, target.slotId).then((result) => {
      if (!active) return;
      if (!result.ok) {
        setLoadError(result.reason);
        return;
      }
      setLineage(result.lineage);
      const first = result.lineage.seats[0];
      if (first !== undefined) {
        setStartInput(toLocalInput(first.scheduledStart));
        setEndInput(toLocalInput(first.scheduledEnd));
      }
    });
    return () => {
      active = false;
    };
  }, [client, accountId, target]);

  const blockedAfterStart =
    lineage !== null && !lineage.canReviseBounds && !lineage.weekEnded && lineage.seats.length > 0;
  const weekEnded = lineage?.weekEnded === true;

  async function confirmRevise(): Promise<void> {
    if (busy || lineage === null) return;
    const startsAt = fromLocalInput(startInput);
    const endsAt = fromLocalInput(endInput);
    const headcount = Number.parseInt(headcountInput, 10);
    if (startsAt === null || endsAt === null || !Number.isSafeInteger(headcount) || headcount < 1) {
      setOutcome({
        ok: false,
        kind: "refused",
        reason: "Enter a valid start time, end time, and headcount.",
      });
      return;
    }
    setBusy(true);
    try {
      const result = await reviseScheduleSlotBounds(
        client,
        accountId,
        target.slotId,
        lineage.expectedRevision,
        startsAt,
        endsAt,
        headcount,
      );
      setOutcome(result);
      if (result.ok) {
        onRevised();
      }
    } catch {
      setOutcome({ ok: false, kind: "unavailable", reason: SAFE_REVISE_BOUNDS_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  const succeeded = outcome !== null && outcome.ok;

  return (
    <div className="minter__confirm" role="dialog" aria-modal="true" aria-label={`Revise times - ${target.siteName}, ${target.routeName}`}>
      <h3 className="minter__confirm-title">Revise times - {target.routeName}</h3>
      <p className="builder__drawer-slot">
        {target.siteName}
        <span className="builder__window">{target.window}</span>
        <span className="builder__fraction">{target.fraction}</span>
      </p>
      {weekEnded ? (
        <p className="surface__state" role="status">
          This week has ended. Review it in <Link to="/schedule">Schedule</Link>,{" "}
          <Link to={scheduleTimesheetsPath()}>Timesheets</Link>.
        </p>
      ) : blockedAfterStart ? (
        <p className="surface__state" role="status">
          {SCHEDULE_SEAT_STARTED}
        </p>
      ) : null}
      {loadError !== null ? (
        <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
          {loadError}
        </p>
      ) : lineage === null && !weekEnded && !blockedAfterStart ? (
        <p className="surface__state" role="status">
          Loading slot bounds...
        </p>
      ) : null}
      {outcome !== null ? (
        outcome.ok ? (
          <p className="builder__drawer-ok" role="status">
            Slot times and headcount revised.
          </p>
        ) : (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {outcome.reason === SCHEDULE_WEEK_ENDED ? (
              <>
                This week has ended. Review it in <Link to="/schedule">Schedule</Link>,{" "}
                <Link to={scheduleTimesheetsPath()}>Timesheets</Link>.
              </>
            ) : (
              outcome.reason
            )}
          </p>
        )
      ) : null}
      {lineage !== null && lineage.canReviseBounds && !succeeded ? (
        <div className="builder__revise-form">
          <p className="builder__drawer-note">Start and end use your device time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}. The host validates the saved UTC bounds.</p>
          <label className="builder__revise-field">
            Start
            <input
              type="datetime-local"
              value={startInput}
              onChange={(event) => setStartInput(event.target.value)}
              disabled={busy}
            />
          </label>
          <label className="builder__revise-field">
            End
            <input
              type="datetime-local"
              value={endInput}
              onChange={(event) => setEndInput(event.target.value)}
              disabled={busy}
            />
          </label>
          <label className="builder__revise-field">
            Headcount
            <input
              type="number"
              min={1}
              max={100}
              value={headcountInput}
              onChange={(event) => setHeadcountInput(event.target.value)}
              disabled={busy}
            />
          </label>
          <p className="builder__drawer-note">
            Previously {target.window}, {target.fraction}.
          </p>
        </div>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
        {lineage !== null && lineage.canReviseBounds && !succeeded ? (
          <button type="button" className="minter__confirm-go" onClick={() => void confirmRevise()} disabled={busy}>
            Save changes
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Member picker + the surface's single write. Loads the redacted-label picker on open;
 * one in-flight assignment at a time; success shows a receipt (plus non-blocking credential
 * warnings) and refetches the week behind it. Raw RPC errors never render - the data layer
 * already maps them to allowlisted-or-safe copy.
 */
function AssignDrawer({
  client,
  accountId,
  target,
  onClose,
  onAssigned,
}: {
  client: SupabaseClient;
  accountId: string;
  target: AssignTarget;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [members, setMembers] = useState<AssignableMember[] | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [busy, setBusy] = useScheduleBusy();
  const [outcome, setOutcome] = useState<AssignOutcome | null>(null);

  useEffect(() => {
    let active = true;
    setMembers(null);
    setPickerError(null);
    setOutcome(null);
    fetchAssignableMembers(client, accountId)
      .then((loaded) => {
        if (active) setMembers(loaded);
      })
      .catch((caught: unknown) => {
        if (active) setPickerError(caught instanceof Error ? caught.message : "Could not load the member picker.");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, target.slotId]);

  async function assign(member: AssignableMember): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const result = await assignMemberToShiftSlot(client, accountId, target.slotId, member.guardMembershipId);
      setOutcome(result);
      if (result.ok) {
        onAssigned();
      }
    } catch {
      setOutcome({ ok: false, reason: SAFE_ASSIGN_FAILURE });
    } finally {
      setBusy(false);
    }
  }

  const assigned = outcome !== null && outcome.ok;
  return (
    <aside className="builder__drawer" role="dialog" aria-modal="true" aria-label={`Assign member - ${target.siteName}, ${target.routeName}`}>
      <div className="builder__drawer-head">
        <h3 className="builder__drawer-title">Assign member</h3>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
      <p className="builder__drawer-slot">
        {target.siteName} - {target.routeName}
        <span className="builder__window">{target.window}</span>
        <span className="builder__fraction">{target.fraction}</span>
      </p>
      {outcome !== null ? (
        outcome.ok ? (
          <div className="builder__drawer-receipt" role="status">
            <p className="builder__drawer-ok">Assigned - status {humanizeLabel(outcome.assignmentStatus)}.</p>
            {outcome.credentialWarningTypes.length > 0 ? (
              <ul className="builder__drawer-warnings">
                {outcome.credentialWarningTypes.map((warning) => (
                  <li key={warning} className="schedule__chip schedule__chip--credential">
                    {humanizeLabel(warning)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {outcome.reason}
          </p>
        )
      ) : null}
      {!assigned ? (
        pickerError !== null ? (
          <p className="surface__state surface__state--error" role="alert" tabIndex={-1}>
            {pickerError}
          </p>
        ) : members === null ? (
          <p className="surface__state" role="status">
            Loading members...
          </p>
        ) : members.length === 0 ? (
          <p className="surface__state" role="status">
            No active guards, supervisors, or admins in this account yet.
          </p>
        ) : (
          <ul className="builder__drawer-guards">
            {members.map((member) => {
              const picked = resolveGuardName({
                displayName: member.guardDisplayName,
                label: member.guardLabel,
                ref: member.guardRef,
                fallback: "Member",
              });
              return (
                <li key={member.guardMembershipId}>
                  <button
                    type="button"
                    className="builder__drawer-guard"
                    disabled={busy}
                    title={guardRefTitle(picked.ref)}
                    onClick={() => void assign(member)}
                  >
                    <span className="builder__drawer-guard-label">{picked.name}</span>
                    <span className={`schedule__chip schedule__chip--role schedule__chip--role-${member.role}`}>
                      {humanizeLabel(member.role)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </aside>
  );
}
